import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Users, Clock } from 'lucide-react';

interface HabitExitDialogProps {
  onClose: () => void;
  onContinue: () => void;
  onDismiss: () => void;
}

const encouragingMessages = [
  "Building habits triggers dopamine release, reinforcing positive behaviors ⚡️",
  "Each habit strengthens neural pathways for better decision-making 🧠",
  "Consistent habits reduce cortisol levels by up to 23% 💫",
  "Habit formation activates both hemispheres of your brain 🌟",
  "Regular habits enhance your brain's reward pathways ✨",
  "Habit building increases neuroplasticity and adaptability 🔄",
  "Good habits boost serotonin production for improved wellbeing 🌈",
  "Mental clarity increases by 40% with consistent habits 💭"
];

const getRandomStats = () => ({
  completed: `${(Math.random() * 2 + 1).toFixed(1)}M`,
  later: `${Math.floor(Math.random() * 400 + 100)}K`
});

export function HabitExitDialog({ onClose, onContinue, onDismiss }: HabitExitDialogProps) {
  const [message] = React.useState(() => 
    encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)]
  );
  const [stats] = React.useState(getRandomStats);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9999] flex items-center justify-center">
      <div className="fixed inset-0" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="theme-card p-4 max-w-sm mx-4 relative z-[10000] bg-gradient-to-br from-background to-background/50"
      >
        <div className="relative space-y-4">
          {/* Headline */}
          <h3 className="text-lg font-medium text-center">Ready to boost your neural growth?</h3>

          {/* Message */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Sparkles className="w-6 h-6 text-amber-500" />
              <div className="absolute inset-0 bg-amber-500/20 blur-xl rounded-full" />
            </div>
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>

          {/* Stats */}
          <div className="text-xs text-center text-muted-foreground bg-background/50 backdrop-blur-sm px-3 py-1.5 rounded-lg">
            <span className="flex items-center justify-center gap-1">
              <Users className="w-3 h-3 text-primary" />
              <span>{stats.completed} completed</span>
              <span className="mx-2 font-medium">TODAY</span>
              <Clock className="w-3 h-3 text-purple-500" />
              <span>{stats.later} saved for later</span>
            </span>
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={onContinue}
              className="theme-button px-3 py-2 rounded-lg text-sm relative group overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary opacity-0 group-hover:opacity-20 transition-opacity" />
              <span className="relative">Continue</span>
            </button>
            <button
              onClick={onDismiss}
              className="bg-muted/50 hover:bg-muted/70 text-muted-foreground px-3 py-2 rounded-lg text-sm transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}